import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MessageSquare, Navigation, Loader2, Mic, MicOff,
  BadgeCheck, Wrench, CreditCard, Star, FileText, AlertTriangle, ChevronDown,
} from "lucide-react";
import { SmsDialog } from "../admin/SmsDialog";
import { SignaturePad } from "./SignaturePad";
import { InspectionChecklist } from "./InspectionChecklist";
import { EstimateForm } from "./EstimateForm";
import { useDictation } from "../../hooks/useDictation";
import { firstName, fmtDateOnly, fmtTime12, smsHref, reviewSmsHref } from "../admin/leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-2.5 h-10 text-sm w-full";

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="font-heading font-semibold text-white text-sm">{title}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const FLOW = [
  { action: "en_route", label: "En Route", sms: true },
  { action: "running_late", label: "Running Late" },
  { action: "arrived", label: "Arrived" },
  { action: "start", label: "Start Job (Clock In)" },
  { action: "pause", label: "Pause" },
  { action: "resume", label: "Resume" },
  { action: "waiting_parts", label: "Waiting on Parts" },
];

export const JobDetail = ({ jobId, tech, onBack }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [vehicle, setVehicle] = useState({});
  const [signature, setSignature] = useState(null);
  const [signName, setSignName] = useState("");
  const [sms, setSms] = useState(null); // {title, message, onLogged}
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [decoding, setDecoding] = useState(false);

  const fetchJob = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tech/jobs/${jobId}`);
      setData(res.data);
      const a = res.data.appointment;
      setForm({
        work_notes: a.work_notes || "", mileage_start: a.mileage_start || "",
        mileage_end: a.mileage_end || "", parts_used: a.parts_used || "",
        inspection: a.inspection || {},
      });
      setVehicle(a.vehicle_info || {});
    } catch {
      toast.error("Could not load job");
      onBack();
    }
  }, [jobId, onBack]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  const { listening, toggle } = useDictation((text) =>
    setForm((f) => ({ ...f, work_notes: `${f.work_notes ? f.work_notes + " " : ""}${text}` }))
  );

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  const appt = data.appointment;
  const first = firstName(appt.name);

  const doAction = async (action) => {
    try {
      const res = await axios.post(`${API}/tech/jobs/${jobId}/action`, { action });
      setData((d) => ({ ...d, appointment: res.data }));
      if (action === "en_route") {
        setSms({
          title: "Text Customer — On My Way",
          message: `Hi ${first}, this is ${tech.name} from Mason's Mobile Mechanic — I'm on my way to you now for your ${appt.service_type}. See you soon!`,
        });
      }
      if (action === "running_late") {
        toast.success("Customer and admin have been notified that you're running late");
      } else {
        toast.success(`Status: ${res.data.status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const saveWork = async (extra = {}) => {
    try {
      const res = await axios.patch(`${API}/tech/jobs/${jobId}`, { ...form, vehicle_info: vehicle, ...extra });
      setData((d) => ({ ...d, appointment: res.data }));
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const complete = async () => {
    if (signature) {
      await saveWork({ signature: { image: signature, printed_name: signName || appt.name } });
    } else {
      await saveWork();
    }
    try {
      const res = await axios.post(`${API}/tech/jobs/${jobId}/action`, { action: "complete" });
      setData((d) => ({ ...d, appointment: res.data }));
      toast.success("Job completed! Send the payment link below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not complete job");
    }
  };

  const recommend = async () => {
    const service = window.prompt("Recommended service (e.g. Brake pads front):");
    if (!service) return;
    const date = window.prompt("Recommend by date (YYYY-MM-DD, optional):") || null;
    try {
      await axios.post(`${API}/tech/jobs/${jobId}/recommend`, { service_type: service, next_service_date: date });
      toast.success("Recommendation saved — admin reminder created");
      fetchJob();
    } catch {
      toast.error("Could not save recommendation");
    }
  };

  const requestApproval = async () => {
    const desc = window.prompt("Additional work needed (description):");
    if (!desc) return;
    const cost = window.prompt("Estimated cost (optional):") || null;
    try {
      await axios.post(`${API}/tech/jobs/${jobId}/request-approval`, { description: desc, estimated_cost: cost });
      toast.success("Approval request sent to admin");
      fetchJob();
    } catch {
      toast.error("Could not send request");
    }
  };

  const decodeVin = async () => {
    if (!vehicle.vin || vehicle.vin.length < 11) return toast.error("Enter a valid VIN first");
    setDecoding(true);
    try {
      const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vehicle.vin.trim()}?format=json`);
      const json = await res.json();
      const r = json.Results?.[0] || {};
      const engine = [r.EngineCylinders && `${r.EngineCylinders}-cyl`, r.DisplacementL && `${Number(r.DisplacementL).toFixed(1)}L`].filter(Boolean).join(" ");
      setVehicle((v) => ({ ...v, year: r.ModelYear || v.year, make: r.Make || v.make, model: r.Model || v.model, engine: engine || v.engine }));
      toast.success("VIN decoded");
    } catch {
      toast.error("VIN decode failed");
    } finally {
      setDecoding(false);
    }
  };

  const mapsUrl = appt.address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(appt.address)}` : null;
  const logSms = async (message) => {
    await axios.post(`${API}/tech/jobs/${jobId}/log-sms`, { message, channel: "sms" }).catch(() => {});
    toast.success("Message logged");
  };

  return (
    <div className="space-y-4" data-testid="tech-job-detail">
      <button data-testid="job-back-button" onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm">
        <ArrowLeft className="h-4 w-4" /> All Jobs
      </button>

      <div className="bg-[#121212] border border-blue-500/30 rounded-xl p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">{appt.name}</h2>
            <div className="text-blue-400 text-sm">{fmtDateOnly(appt.date)} · {fmtTime12(appt.start_time)} · {appt.service_type}</div>
            {data.membership_status === "active" && (
              <span className="inline-flex items-center gap-1 text-emerald-400 text-xs mt-1"><BadgeCheck className="h-3.5 w-3.5" /> Active Member — 10% off eligible labor</span>
            )}
          </div>
          <span className="text-xs font-bold uppercase bg-blue-600/20 text-blue-300 border border-blue-500/40 rounded-sm px-2 py-1" data-testid="job-status-badge">{appt.status}</span>
        </div>
        <div className="flex gap-2 mt-4">
          <a data-testid="job-call-button" href={`tel:${appt.phone.replace(/[^+\d]/g, "")}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-600 text-white rounded-sm py-2.5 text-sm font-semibold"><Phone className="h-4 w-4" /> Call</a>
          <a data-testid="job-text-button" href={smsHref(appt.phone, `Hi ${first}, this is ${tech.name} from Mason's Mobile Mechanic. `)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-700 text-white rounded-sm py-2.5 text-sm font-semibold"><MessageSquare className="h-4 w-4" /> Text</a>
          {mapsUrl && <a data-testid="job-navigate-button" href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-700 text-white rounded-sm py-2.5 text-sm font-semibold"><Navigation className="h-4 w-4" /> Navigate</a>}
        </div>
        {appt.address && <div className="text-zinc-500 text-xs mt-2">{appt.address}</div>}
        {appt.notes && <div className="text-zinc-400 text-xs mt-2 border-t border-zinc-800 pt-2">Admin notes: {appt.notes}</div>}
      </div>

      {appt.status !== "Completed" && (
        <div className="grid grid-cols-2 gap-2" data-testid="job-action-buttons">
          {FLOW.map((f) => (
            <button key={f.action} data-testid={`job-action-${f.action}`} onClick={() => doAction(f.action)} className="border border-zinc-700 hover:border-blue-500 text-white rounded-sm py-3 text-sm font-semibold transition-colors">
              {f.label}
            </button>
          ))}
        </div>
      )}

      <Section title="Vehicle Info & VIN Decode">
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 flex gap-2">
            <input data-testid="vehicle-vin-input" className={inputCls} placeholder="VIN" value={vehicle.vin || ""} onChange={(e) => setVehicle((v) => ({ ...v, vin: e.target.value }))} />
            <button data-testid="vehicle-vin-decode" onClick={decodeVin} disabled={decoding} className="shrink-0 bg-blue-600 text-white rounded-sm px-4 text-xs font-semibold disabled:opacity-60">
              {decoding ? "..." : "Decode VIN"}
            </button>
          </div>
          {["year", "make", "model", "engine"].map((k) => (
            <input key={k} data-testid={`vehicle-${k}-input`} className={inputCls} placeholder={k[0].toUpperCase() + k.slice(1)} value={vehicle[k] || ""} onChange={(e) => setVehicle((v) => ({ ...v, [k]: e.target.value }))} />
          ))}
        </div>
      </Section>

      <Section title="Work Notes, Mileage & Parts" defaultOpen>
        <div className="space-y-2">
          <div className="relative">
            <textarea data-testid="job-work-notes" className="w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm p-2.5 text-sm h-28" placeholder="Work performed..." value={form.work_notes} onChange={(e) => setForm((f) => ({ ...f, work_notes: e.target.value }))} />
            <button data-testid="job-dictate-button" onClick={toggle} className={`absolute bottom-3 right-2 p-2 rounded-sm ${listening ? "bg-red-600 text-white animate-pulse" : "border border-zinc-700 text-zinc-400"}`} title="Voice dictation">
              {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="job-mileage-start" className={inputCls} placeholder="Starting mileage" value={form.mileage_start} onChange={(e) => setForm((f) => ({ ...f, mileage_start: e.target.value }))} />
            <input data-testid="job-mileage-end" className={inputCls} placeholder="Ending mileage" value={form.mileage_end} onChange={(e) => setForm((f) => ({ ...f, mileage_end: e.target.value }))} />
          </div>
          <input data-testid="job-parts-used" className={inputCls} placeholder="Parts used" value={form.parts_used} onChange={(e) => setForm((f) => ({ ...f, parts_used: e.target.value }))} />
          <div className="flex gap-2">
            <button data-testid="job-save-work" onClick={() => saveWork()} className="bg-blue-600 text-white rounded-sm px-4 py-2 text-xs font-semibold">Save</button>
            <button data-testid="job-recommend-button" onClick={recommend} className="border border-cyan-500/50 text-cyan-400 rounded-sm px-3 py-2 text-xs font-semibold inline-flex items-center gap-1"><Wrench className="h-3.5 w-3.5" /> Recommend Service</button>
            <button data-testid="job-request-approval" onClick={requestApproval} className="border border-amber-500/50 text-amber-400 rounded-sm px-3 py-2 text-xs font-semibold inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Request Approval</button>
          </div>
          {(appt.recommendations || []).map((r) => (
            <div key={r.id} className="text-xs border border-cyan-500/30 rounded-sm p-2 flex justify-between" data-testid={`job-recommendation-${r.id}`}>
              <span className="text-cyan-300">Recommended: {r.service_type}{r.next_service_date ? ` by ${r.next_service_date}` : ""}</span>
              <span className="text-zinc-600">{r.by}</span>
            </div>
          ))}
          {(appt.approval_requests || []).map((r) => (
            <div key={r.id} className="text-xs border border-zinc-800 rounded-sm p-2 flex justify-between">
              <span className="text-zinc-300">{r.description}{r.estimated_cost ? ` (~$${r.estimated_cost})` : ""}</span>
              <span className={r.status === "Approved" ? "text-green-400" : r.status === "Declined" ? "text-red-400" : "text-amber-400"}>{r.status}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Vehicle Inspection Checklist">
        <InspectionChecklist value={form.inspection} onChange={(insp) => setForm((f) => ({ ...f, inspection: insp }))} />
        <button onClick={() => saveWork()} className="mt-2 bg-blue-600 text-white rounded-sm px-4 py-2 text-xs font-semibold">Save Inspection</button>
      </Section>

      <Section title="Estimate (send for e-signature)">
        {estimateOpen ? (
          <EstimateForm job={appt} onSaved={() => {}} />
        ) : (
          <button data-testid="job-create-estimate" onClick={() => setEstimateOpen(true)} className="inline-flex items-center gap-1.5 border border-zinc-700 text-white rounded-sm px-4 py-2.5 text-sm font-semibold"><FileText className="h-4 w-4" /> Create Estimate</button>
        )}
      </Section>

      <Section title="Prior Service History">
        {data.history.length === 0 ? <p className="text-zinc-600 text-xs">No prior visits found.</p> : (
          <div className="space-y-2">
            {data.history.map((h, i) => (
              <div key={i} className="text-xs border border-zinc-800 rounded-sm p-2">
                <span className="text-zinc-300">{fmtDateOnly(h.date)} — {h.service_type}</span>
                <span className="text-zinc-600"> · {h.status}{h.technician_name ? ` · ${h.technician_name}` : ""}</span>
                {h.work_notes && <div className="text-zinc-500 mt-0.5">{h.work_notes}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {appt.status !== "Completed" ? (
        <div className="bg-[#121212] border border-green-500/30 rounded-xl p-5 space-y-3" data-testid="job-complete-section">
          <h3 className="font-heading font-semibold text-white text-sm">Complete Job — Customer Signature (optional)</h3>
          <SignaturePad onChange={setSignature} height={120} />
          <input data-testid="job-signature-name" className={inputCls} placeholder="Customer printed name" value={signName} onChange={(e) => setSignName(e.target.value)} />
          <button data-testid="job-complete-button" onClick={complete} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-sm py-3.5 font-semibold">
            Complete Job (Clock Out)
          </button>
        </div>
      ) : (
        <div className="bg-[#121212] border border-green-500/30 rounded-xl p-5 space-y-2" data-testid="job-completed-actions">
          <p className="text-green-400 text-sm font-semibold">✓ Job completed{appt.labor_minutes ? ` — ${appt.labor_minutes} min on the clock` : ""}</p>
          <button
            data-testid="job-payment-link-button"
            onClick={() => setSms({
              title: "Send Payment Link",
              message: `Hi ${first}, thank you for choosing Mason's Mobile Mechanic! You can pay securely online here: ${window.location.origin}/pay — Just enter the amount from your invoice. Questions? Call or text (404) 749-7063.`,
            })}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white rounded-sm py-3 text-sm font-semibold"
          >
            <CreditCard className="h-4 w-4" /> Text Payment Link
          </button>
          <a
            data-testid="job-review-link-button"
            href={reviewSmsHref({ name: appt.name, phone: appt.phone })}
            className="w-full inline-flex items-center justify-center gap-2 border border-zinc-700 text-white rounded-sm py-3 text-sm font-semibold"
          >
            <Star className="h-4 w-4" /> Text Review Request
          </a>
        </div>
      )}

      {sms && (
        <SmsDialog key={sms.title} open onOpenChange={(v) => !v && setSms(null)} phone={appt.phone} title={sms.title} initialMessage={sms.message} onLogged={logSms} />
      )}
    </div>
  );
};
